import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';
import {
  downloadBulkOrderTemplateAPI,
  downloadBulkPropertyTemplateAPI,
  getFileFailureDataAPI,
} from '../service/apiService';
import { downloadBlob } from '@/util/downloadFile';
import { useNavigationService } from '@/services/navigate-service';
import { ROUTES } from '@/config/routes';
import { getFailedTableData } from './orderErrorParser';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BulkUploadFileResponse {
  fileId: string;
  fileName: string;
  fileNumber: number;
  uploadedBy: string;
  uploadedDate: string;
  orderRejected: number;
  orderApproved: number;
  subTotal: number;
  [key: string]: unknown;
}

export interface UploadApiResponse {
  fileName: string;
  uploadedDate: string;
  // property-service PropertyBulkUploadRequest: count added / count failed
  orderPlaced?: number;
  orderRejected?: number;
  [key: string]: unknown;
}

export interface BulkImportCallbacks {
  setFileId: (v: string | null) => void;
  setAmount: (v: number) => void;
  setOrderCountInfo: (v: string | number) => void;
  setUploadedByInfo: (v: string) => void;
  setFileName: (v: string) => void;
  setDateInfo: (v: string) => void;
  setTableData: (v: string | null) => void;
  setSuccessData: (v: string | null) => void;
  setFailedData: (v: string | null) => void;
  setActiveStep: (v: number) => void;
  setSkippedActiveStep?: (v: number | undefined) => void;
  setFromTableDataFromNextPage: (v: boolean) => void;
  setFixErrorTableData: (v: string | null) => void;
  setSkippedStepFromStepper?: (v: number | undefined) => void;
  setFileState: (v: boolean | null) => void;
  setFileNumber: (v: number | undefined) => void;
  setShowStep3: (v: boolean) => void;
  setFileUploadDisabled: (v: boolean) => void;
  setIsContinue: (v: boolean) => void;
}

export interface BulkImportStateValues {
  fileId: string | null;
  uploadedByInfo: string | undefined;
  fileName: string | undefined;
  tableData: string | null;
  amount: number | undefined;
  orderCountInfo: string | number | undefined;
  successData: string | null;
  failedData: string | null;
  fromTableDataFromNextPage: boolean;
  showStep3: boolean;
  fileNumber: number | undefined;
  fileUploadDisabled: boolean;
  isContinue: boolean;
}

export interface UseBulkImportOptions {
  isProperty: boolean;
  callbacks: BulkImportCallbacks;
  stateValues: BulkImportStateValues;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useBulkImport({
  isProperty,
  callbacks,
  stateValues
}: UseBulkImportOptions) {
  const dispatch = useDispatch();
  const { navigateTo } = useNavigationService();

  const [responseData, setResponseData] = useState<UploadApiResponse | null>(
    null
  );
  const [uploadFileData, setUploadFileData] =
    useState<BulkUploadFileResponse | null>(null);
  const [failedCountInfo, setFailedCountInfo] = useState<string | null>(
    stateValues.failedData
  );
  const [fileIdInfo, setFileIdInfo] = useState<string | null>(
    stateValues.fileId
  );
  const [fileNameInfo, setFileNameInfo] = useState<string | undefined>(
    stateValues.fileName
  );
  const [orderCount, setOrderCount] = useState<number | undefined>(
    stateValues.orderCountInfo as number | undefined
  );
  const [orderTotalValue, setOrderTotalValue] = useState<number | undefined>(
    stateValues.amount
  );
  const [inProgress, setInProgress] = useState(false);
  const [disabledUploadProperty, setDisabledUploadProperty] = useState(false);

  // ---- Side effects ----

  const checkFailedData = useCallback(() => {
    if (!uploadFileData) return;
    setFileIdInfo(uploadFileData.fileId);
    callbacks.setFileName(uploadFileData.fileName);
    callbacks.setFileNumber(uploadFileData.fileNumber);
    setFileNameInfo(uploadFileData.fileName);
    const total =
      uploadFileData.orderRejected + uploadFileData.orderApproved;
    setOrderCount(total);
    callbacks.setOrderCountInfo(uploadFileData.orderApproved);
    setOrderTotalValue(uploadFileData.subTotal);
    callbacks.setAmount(uploadFileData.subTotal);
    getFailedDataOfFile(uploadFileData.fileId);
    // callbacks / setters / getFailedDataOfFile are stable refs; only
    // uploadFileData drives the computation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFileData]);

  useEffect(() => {
    if (uploadFileData !== null) {
      checkFailedData();
    }
  }, [uploadFileData, checkFailedData]);

  useEffect(() => {
    if (
      (stateValues.successData != null &&
        Number(stateValues.successData) >= 0) ||
      (stateValues.failedData != null &&
        Number(stateValues.failedData) >= 0)
    ) {
      if (!stateValues.fromTableDataFromNextPage) {
        callbacks.setIsContinue(false);
        callbacks.setShowStep3(true);
      }
    }
    // Mount-only: snapshot stateValues once and decide whether to skip ahead
    // to step 3 based on hydrated success/failure counts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      stateValues.tableData !== null &&
      !stateValues.fromTableDataFromNextPage
    ) {
      if (!isProperty) {
        callbacks.setShowStep3(true);
      }
    }
    // Reacts to tableData arriving; isProperty / fromTableDataFromNextPage /
    // callbacks are signal-stable for the file's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateValues.tableData]);

  // ---- API call helpers ----

  const getFailedDataOfFile = async (fileIdVal: string) => {
    try {
      const fileData = await getFileFailureDataAPI(fileIdVal, isProperty);
      if (fileData) {
        const { result, failedCount, successCount } = getFailedTableData(
          fileData.data,
          isProperty
        );

        if (uploadFileData) {
          callbacks.setFileId(uploadFileData.fileId);
          callbacks.setUploadedByInfo(uploadFileData.uploadedBy);
          callbacks.setDateInfo(uploadFileData.uploadedDate);
          callbacks.setFileName(uploadFileData.fileName);
        }
        callbacks.setSuccessData(successCount.toString());
        callbacks.setFileState(false);
        callbacks.setFailedData(failedCount.toString());
        setFailedCountInfo(failedCount.toString());
        callbacks.setTableData(JSON.stringify(result));
        callbacks.setFixErrorTableData(JSON.stringify(result));
        callbacks.setIsContinue(false);

        setInProgress(false);
        callbacks.setShowStep3(true);
      }
    } catch (err: unknown) {
      callbacks.setFileUploadDisabled(false);
      setDisabledUploadProperty(false);
      setInProgress(false);
      dispatch(
        showAlert({ type: Variants.ERROR, apiResponse: err })
      );
    }
  };

  // ---- Event handlers ----

  // Confirm a fully-clean upload with a toast. Records that need fixing already
  // surface via the "Fix errors" row in Import History, so we only celebrate
  // when nothing was rejected (and at least one row landed).
  const notifyCleanUpload = (opts: {
    added: number | undefined;
    rejected: number | undefined;
    noun: string;
    nounPlural: string;
    verb: string;
  }) => {
    const { added, rejected, noun, nounPlural, verb } = opts;
    if (rejected || !added || added <= 0) return;
    dispatch(
      showAlert({
        type: Variants.SUCCESS,
        message: `${added} ${added === 1 ? noun : nounPlural} ${verb} successfully.`,
      })
    );
  };

  const onFileUpload = (response: UploadApiResponse) => {
    setResponseData(response);
    notifyCleanUpload({
      added: response.orderPlaced,
      rejected: response.orderRejected,
      noun: 'property',
      nounPlural: 'properties',
      verb: 'imported',
    });
  };

  const onFileUploadForOrder = (response: BulkUploadFileResponse) => {
    setUploadFileData(response);
    notifyCleanUpload({
      added: response.orderApproved,
      rejected: response.orderRejected,
      noun: 'order',
      nounPlural: 'orders',
      verb: 'uploaded',
    });
  };

  const onBack = () => navigateTo(ROUTES.ORDERS);
  const onNext = () => callbacks.setActiveStep(2);
  const onCancel = () => navigateTo(ROUTES.PROPERTIES);

  const getFileName = (): string => (isProperty ? 'property' : 'order');

  const showImportHistory = (): boolean => {
    if (isProperty) return true;
    return !stateValues.showStep3;
  };

  const onTemplateDownload = async () => {
    try {
      const response = isProperty
        ? await downloadBulkPropertyTemplateAPI()
        : await downloadBulkOrderTemplateAPI();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const fileName = isProperty ? 'property_template.xlsx' : 'bulk_order_template.xlsx';
      downloadBlob(blob, fileName);
    } catch (err: unknown) {
      dispatch(
        showAlert({
          type: Variants.ERROR,
          message: 'Failed to download template. Please try again.',
        }),
      );
    }
  };

  return {
    responseData,
    uploadFileData,
    setUploadFileData,
    failedCountInfo,
    fileIdInfo,
    fileNameInfo,
    orderCount,
    orderTotalValue,
    setOrderTotalValue,
    inProgress,
    setInProgress,
    disabledUploadProperty,
    setDisabledUploadProperty,
    onFileUpload,
    onFileUploadForOrder,
    onBack,
    onNext,
    onCancel,
    onTemplateDownload,
    getFileName,
    showImportHistory
  };
}
